import { useEffect, useMemo } from 'react'
import { useStore } from '@/store/Store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Trash2, Plus, Minus } from 'lucide-react'
import { toast } from 'sonner'
import type { OverpayPolicy, PaymentMethod } from '@/types/backend'
import CustomerBar from '../customers/CustomerBar'
import { formatCFA } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { useCustomerQuery } from '@/hooks/useCustomers'

type Props = {
  stockById: Map<string, number>
  onValidated?: () => void
}

const PM_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Espèces' },
  { value: 'MOBILE', label: 'Mobile Money' },
  { value: 'CARD', label: 'Carte bancaire' },
  { value: 'TRANSFER', label: 'Virement' },
  { value: 'OTHER', label: 'Autre' },
]

export default function CartAside({ stockById, onValidated }: Props) {
  const { state, dispatch } = useStore()
  const { cart } = state

  const { data: selectedCustomer } = useCustomerQuery(
    cart.customerId ?? undefined,
    !!cart.customerId
  )
  const isWalkIn = selectedCustomer?.isWalkIn ?? false

  useEffect(() => {
    if (isWalkIn && cart.initialOverpayPolicy !== 'RETURN') {
      dispatch({
        type: 'CART_SET_META',
        payload: { initialOverpayPolicy: 'RETURN' },
      })
    }
  }, [isWalkIn, cart.initialOverpayPolicy, dispatch])

  const setOverpayPolicy = (p: OverpayPolicy) =>
    dispatch({ type: 'CART_SET_META', payload: { initialOverpayPolicy: p } })

  const subTotal = useMemo(
    () => cart.items.reduce((a, it) => a + it.unitPrice * it.quantity, 0),
    [cart.items]
  )
  const total = subTotal + (cart.tax ?? 0)
  const given = cart.initialPaymentAmount ?? 0
  const overpay = Math.max(0, given - total)
  const policy = cart.initialOverpayPolicy ?? 'APPLY_TO_DEBT'
  const effectiveChange = policy === 'RETURN' ? overpay : 0

  const setQty = (index: number, next: number) => {
    const it = cart.items[index]
    if (!it) return
    const base = it.itemId ? stockById.get(it.itemId) ?? Infinity : Infinity
    const clamped = Math.max(1, Math.min(next, base))
    if (clamped !== next && base !== Infinity) {
      toast.error('Stock insuffisant', { description: `Max: ${base}` })
    }
    dispatch({ type: 'CART_UPDATE_ITEM', index, patch: { quantity: clamped } })
  }

  const inc = (i: number, delta: number) =>
    setQty(i, cart.items[i].quantity + delta)

  const remove = (i: number) => dispatch({ type: 'CART_REMOVE_ITEM', index: i })
  const clear = () => dispatch({ type: 'CART_CLEAR' })

  const setTax = (val: number) =>
    dispatch({ type: 'CART_SET_META', payload: { tax: val } })

  const setInitialPayment = (val: number) =>
    dispatch({ type: 'CART_SET_META', payload: { initialPaymentAmount: val } })

  const setMethod = (m: PaymentMethod) =>
    dispatch({ type: 'CART_SET_META', payload: { initialPaymentMethod: m } })

  const setCustomer = (customerId: string) =>
    dispatch({ type: 'CART_SET_META', payload: { customerId } })

  return (
    <>
      <CustomerBar />
      <div className='border rounded-xl p-4 bg-background/50 sticky top-4 mt-4'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-lg font-semibold'>Panier</h3>
          <Button
            variant='outline'
            size='sm'
            onClick={clear}
            disabled={cart.items.length === 0}
          >
            Vider
          </Button>
        </div>

        <div className='space-y-3 max-h-[50vh] overflow-auto pr-1'>
          {cart.items.length === 0 && (
            <div className='text-sm text-muted-foreground'>Aucun article</div>
          )}
          {cart.items.map((it, i) => {
            const base = it.itemId
              ? stockById.get(it.itemId) ?? Infinity
              : Infinity
            return (
              <div
                key={`${it.itemId ?? it.name}-${i}`}
                className='border rounded-md p-3'
              >
                <div className='flex items-start justify-between gap-2'>
                  <div className='flex-1'>
                    <div className='font-medium leading-5'>{it.name}</div>
                    <div className='text-xs text-muted-foreground'>
                      PU: {it.unitPrice.toLocaleString()} — Stock:{' '}
                      {base === Infinity ? '—' : base}
                    </div>
                  </div>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => remove(i)}
                    aria-label='Supprimer'
                  >
                    <Trash2 className='h-4 w-4 text-red-500' />
                  </Button>
                </div>

                <div className='mt-2 flex items-center justify-between'>
                  <div className='flex items-center gap-1'>
                    <Button
                      variant='outline'
                      size='icon'
                      onClick={() => inc(i, -100)}
                    >
                      <Minus className='h-4 w-4' />{' '}
                    </Button>
                    <Button
                      variant='outline'
                      size='icon'
                      onClick={() => inc(i, -5)}
                    >
                      <Minus className='h-4 w-4 text-blue-600' />
                    </Button>
                    <Button
                      variant='outline'
                      size='icon'
                      onClick={() => inc(i, -1)}
                    >
                      <Minus className='h-4 w-4' />
                    </Button>
                    <div className='px-3 tabular-nums'>{it.quantity}</div>
                    <Button
                      variant='outline'
                      size='icon'
                      onClick={() => inc(i, +1)}
                    >
                      <Plus className='h-4 w-4' />
                    </Button>
                    <Button
                      variant='outline'
                      size='icon'
                      onClick={() => inc(i, +5)}
                    >
                      <Plus className='h-4 w-4 text-blue-600' />
                    </Button>
                    <Button
                      variant='outline'
                      size='icon'
                      onClick={() => inc(i, +100)}
                    >
                      <Plus className='h-4 w-4 text-red-600' />
                    </Button>
                  </div>
                  <div className='font-medium'>
                    {(it.unitPrice * it.quantity).toLocaleString()}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className='mt-4 space-y-3'>
          <div className='space-y-1'>
            <div className='text-sm'>Client</div>
            <Input
              placeholder='ID client (temporaire)'
              value={cart.customerId ?? ''}
              onChange={(e) => setCustomer(e.target.value)}
            />
          </div>

          <div className='grid gap-3 grid-cols-1 '>
            {/* Frais/Taxes */}
            <div className='space-y-1.5'>
              <Label htmlFor='tax'>Frais / Taxes</Label>
              <div className='relative'>
                <Input
                  id='tax'
                  type='number'
                  inputMode='decimal'
                  step='0.01'
                  className='h-9  pr-10'
                  value={cart.tax ?? 0}
                  onChange={(e) => setTax(parseFloat(e.target.value || '0'))}
                  placeholder='0'
                />
                <span className='pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground'>
                  CFA
                </span>
              </div>
            </div>

            {/* Paiement initial */}
            <div className='space-y-1.5'>
              <Label htmlFor='initial'>Paiement initial</Label>
              <div className='relative'>
                <Input
                  id='initial'
                  type='number'
                  inputMode='decimal'
                  step='0.01'
                  className='h-9  pr-10'
                  value={cart.initialPaymentAmount ?? 0}
                  onChange={(e) =>
                    setInitialPayment(parseFloat(e.target.value || '0'))
                  }
                  placeholder='0'
                />
                <span className='pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground'>
                  CFA
                </span>
              </div>
            </div>

            {/* Surplus */}
            <div className='space-y-1.5'>
              <Label>Surplus</Label>
              <Select
                value={isWalkIn ? 'RETURN' : policy}
                onValueChange={(v) => setOverpayPolicy(v as OverpayPolicy)}
                disabled={isWalkIn}
              >
                <SelectTrigger className='h-9'>
                  <SelectValue placeholder='Choisir…' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='RETURN'>Rendre la monnaie</SelectItem>
                  <SelectItem value='CREDIT'>Mettre en avoir</SelectItem>
                  <SelectItem value='APPLY_TO_DEBT'>
                    Régler les dettes
                  </SelectItem>
                </SelectContent>
              </Select>
              {isWalkIn && (
                <p className='text-xs text-muted-foreground'>
                  Le surplus est toujours rendu en monnaie pour le Client
                  Ordinaire.
                </p>
              )}
            </div>
          </div>

          <div>
            <div className='text-sm mb-1'>Méthode de paiement</div>
            <Select
              value={cart.initialPaymentMethod ?? undefined}
              onValueChange={(v) => setMethod(v as PaymentMethod)}
            >
              <SelectTrigger>
                <SelectValue placeholder='Choisir…' />
              </SelectTrigger>
              <SelectContent>
                {PM_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='border-t pt-3 space-y-1 text-sm'>
            <div className='flex justify-between'>
              <span>Sous-total</span>
              <span>{formatCFA(subTotal)}</span>
            </div>
            <div className='flex justify-between'>
              <span>Frais</span>
              <span>{formatCFA(cart.tax ?? 0)}</span>
            </div>
            <div className='flex justify-between font-semibold text-base'>
              <span>Total</span>
              <span>{formatCFA(total)}</span>
            </div>
            {given > 0 && (
              <>
                {policy === 'RETURN' ? (
                  <div className='flex justify-between text-xs text-muted-foreground'>
                    <span>Monnaie</span>
                    <span>{formatCFA(effectiveChange)}</span>
                  </div>
                ) : overpay > 0 ? (
                  <div className='text-xs text-muted-foreground'>
                    Surplus {formatCFA(overpay)} sera{' '}
                    {policy === 'CREDIT'
                      ? 'mis en avoir client'
                      : 'utilisé pour régler les dettes (plus anciennes d’abord)'}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        <div className='mt-4'>
          <Button
            className='w-full'
            disabled={cart.items.length === 0 || !cart.customerId}
            onClick={() => onValidated?.()}
          >
            Valider la vente
          </Button>
        </div>
      </div>
    </>
  )
}
